/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import DOMPurify from 'dompurify';
import { stableStringHash } from '../../react-components/markdown/markdownContentUtils';
import type {
  AgentWorkspaceItemLayout,
  AgentWorkspaceWidget,
  ExpandedAgentArtifact,
  ExpandedAgentArtifactKind,
} from './types';

export const WORKSPACE_COLUMNS = 12;
export const WORKSPACE_ROW_HEIGHT = 40;
export const WORKSPACE_MARGIN = 12;
/** Widget headers are the grid's drag handle. */
export const WORKSPACE_DRAG_HANDLE_CLASS = 'agent-workspace-drag-handle';

const DEFAULT_WIDGET_SIZE: Record<ExpandedAgentArtifactKind, { w: number; h: number }> = {
  plot: { w: 8, h: 8 },
  table: { w: 6, h: 7 },
  markdown: { w: 4, h: 8 },
  code: { w: 6, h: 8 },
  map: { w: 6, h: 8 },
  mapView: { w: 6, h: 8 },
};

export const ARTIFACT_KIND_LABEL: Record<ExpandedAgentArtifactKind, string> = {
  plot: 'Chart',
  table: 'Table',
  markdown: 'Document',
  code: 'Code',
  map: 'Parcel map',
  mapView: 'Map',
};

/** Plotly titles can contain markup; widget headers use the first nonempty line as text. */
export const toPlainTitle = (value: string) => {
  for (const line of value.split(/<br\s*\/?>/i)) {
    const text = (
      DOMPurify.sanitize(line, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        RETURN_DOM_FRAGMENT: true,
      }).textContent ?? ''
    )
      .replace(/\s+/g, ' ')
      .trim();
    if (text) return text;
  }
  return '';
};

const plotTitle = (layout: unknown) => {
  const title = (layout as { title?: string | { text?: string } } | undefined)?.title;
  const text = typeof title === 'string' ? title : title?.text;
  return text ? toPlainTitle(text) : undefined;
};

export const getArtifactTitle = (artifact: ExpandedAgentArtifact): string => {
  switch (artifact.kind) {
    case 'table':
    case 'markdown':
    case 'code':
      return artifact.value.title;
    case 'map':
      return artifact.value.filename ?? 'Parcel map';
    case 'mapView':
      return artifact.value.title ?? 'Location map';
    case 'plot':
      return plotTitle(artifact.value.layout) || 'Chart';
    default: {
      const unknownArtifact: never = artifact;
      return unknownArtifact;
    }
  }
};

/**
 * Same artifact, same widget: expanding it again focuses the existing widget. Tables carry
 * rendered content only, so each table expansion is its own widget.
 */
export const getArtifactWidgetId = (artifact: ExpandedAgentArtifact): string => {
  switch (artifact.kind) {
    case 'table':
      return `table:${crypto.randomUUID()}`;
    case 'map':
      return `map:${artifact.value.id}`;
    case 'markdown':
      return `markdown:${stableStringHash(artifact.value.content)}`;
    case 'code':
      // The same source in two languages is two blocks (and two transcript parts).
      return `code:${stableStringHash(`${artifact.value.language}\n${artifact.value.content}`)}`;
    case 'mapView':
    case 'plot':
      return `${artifact.kind}:${stableStringHash(JSON.stringify(artifact.value))}`;
    default: {
      const unknownArtifact: never = artifact;
      return unknownArtifact;
    }
  }
};

const overlaps = (a: AgentWorkspaceItemLayout, b: Omit<AgentWorkspaceItemLayout, 'i'>) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** First free slot scanning top-to-bottom, left-to-right, like the grid's own compaction. */
export const placeWidget = (
  layout: ReadonlyArray<AgentWorkspaceItemLayout>,
  i: string,
  kind: ExpandedAgentArtifactKind,
): AgentWorkspaceItemLayout => {
  const { w, h } = DEFAULT_WIDGET_SIZE[kind];
  const bottom = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  for (let y = 0; y <= bottom; y += 1) {
    for (let x = 0; x <= WORKSPACE_COLUMNS - w; x += 1) {
      const candidate = { x, y, w, h };
      if (!layout.some((item) => overlaps(item, candidate))) return { i, ...candidate };
    }
  }
  return { i, x: 0, y: bottom, w, h };
};

/** Default packing for the "Auto-arrange" action, in widget order. */
export const arrangeWidgets = (widgets: ReadonlyArray<AgentWorkspaceWidget>) => {
  const layout: AgentWorkspaceItemLayout[] = [];
  for (const widget of widgets) layout.push(placeWidget(layout, widget.id, widget.artifact.kind));
  return layout;
};
